import { cn } from "@/lib/utils";

/**
 * Progress — plan/27 action feedback.
 * Determinate only when value is real (0–100 or value/max).
 * Indeterminate thin bar when duration unknown — never fake creep.
 */
export function ProgressBar(props: {
  /** 0–100. Omit for indeterminate. */
  value?: number;
  /** Alternate: value/max → percent. */
  current?: number;
  max?: number;
  /** Visible caption above the track. */
  label?: string;
  /** Accessible name when `label` is omitted (e.g. table cells). */
  "aria-label"?: string;
  className?: string;
}) {
  const fromFraction =
    props.current != null &&
    props.max != null &&
    props.max > 0
      ? Math.min(100, Math.max(0, (props.current / props.max) * 100))
      : null;
  const pct =
    props.value != null
      ? Math.min(100, Math.max(0, props.value))
      : fromFraction;
  const indeterminate = pct == null;
  const ariaName =
    props["aria-label"] ??
    props.label ??
    (indeterminate ? "In progress" : "Progress");

  return (
    <div className={cn("w-full space-y-1", props.className)}>
      {props.label ? (
        <p className="m-0 text-xs text-[var(--color-muted)]">{props.label}</p>
      ) : null}
      <div
        className="h-1.5 overflow-hidden rounded-full bg-[var(--color-border)]/80"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : Math.round(pct)}
        aria-label={ariaName}
      >
        {indeterminate ? (
          <div className="h-full w-1/3 animate-[progressSlide_1.1s_ease-in-out_infinite] rounded-full bg-[var(--color-primary)]" />
        ) : (
          <div
            className="h-full rounded-full bg-[var(--color-primary)] transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  );
}
